import { prependZero } from '@backoffice-frontend/common'
import {
  getOrganizationName,
  isImpersonationSession,
} from '@backoffice-frontend/temporary-backoffice-sessions-common'
import { differenceInSeconds, parseISO } from 'date-fns'
import type { ActiveTemporarySession } from './types'

const SECONDS_PER_MINUTE = 60
const SECONDS_PER_HOUR = 60 * SECONDS_PER_MINUTE

/**
 * Seconds left until `expiresAt`, clamped at 0 so an already-expired session
 * never counts into the negative.
 */
export const getSecondsRemaining = (expiresAt: string, now: Date): number =>
  Math.max(0, differenceInSeconds(parseISO(expiresAt), now))

/** `mm:ss` under an hour, `h:mm:ss` from an hour up. */
export const formatCountdown = (seconds: number): string => {
  const safeSeconds = Math.max(0, Math.floor(seconds))

  const hours = Math.floor(safeSeconds / SECONDS_PER_HOUR)
  const minutes = Math.floor(
    (safeSeconds % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE,
  )
  const remainingSeconds = safeSeconds % SECONDS_PER_MINUTE

  if (hours > 0) {
    return `${hours}:${prependZero(minutes)}:${prependZero(remainingSeconds)}`
  }

  return `${prependZero(minutes)}:${prependZero(remainingSeconds)}`
}

/**
 * Display name of the organization a scoped-access session targets, falling
 * back to the raw `organizationId` when it cannot be resolved.
 *
 * Impersonation sessions name the impersonated user instead, so they resolve to
 * `undefined`.
 */
export const resolveOrganizationName = (
  organizations: readonly { id: string; name: string }[] | undefined,
  session: ActiveTemporarySession | undefined,
): string | undefined => {
  if (!session || isImpersonationSession(session)) {
    return undefined
  }

  return getOrganizationName(organizations, session.permissions.organizationId)
}
